import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, EmptyState } from "@/components/Common";
import { UploadCloud, Loader2, Users, Download, X, AlertTriangle, Syringe, Bookmark, Star, Save, Trash2, Mail, Plus, Send, FileText, DollarSign, Archive, ChevronDown, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { toast } from "sonner";

const num = (n) => (parseFloat(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// Keys for the built-in Amended Exhibit A bundles — used to decide whether a
// row is "protected default" (no trash icon) or admin-added (deletable).
// Format: `${family}|${months}|${doses-sorted-comma-joined}`. Dose numbers use
// JS `Number` string repr to match `[...doses].sort().join(",")`.
const DEFAULT_COMBO_KEYS = new Set([
  "Semaglutide|2|0.25,0.5", "Semaglutide|2|1,1.5", "Semaglutide|2|2,2.4", "Semaglutide|2|2.4,2.4",
  "Semaglutide|3|0.25,0.5,1", "Semaglutide|3|1.5,2,2.4", "Semaglutide|3|2.4,2.4,2.4",
  "Tirzepatide|2|2.5,5", "Tirzepatide|2|5,7.5", "Tirzepatide|2|10,12.5", "Tirzepatide|2|15,15",
  "Tirzepatide|3|2.5,5,7.5", "Tirzepatide|3|10,12.5,15", "Tirzepatide|3|15,15,15",
]);

const FAMILY_COLOR = { Tirzepatide: "#0f766e", Semaglutide: "#a3195b", Other: "#64748b" };
const FAMILY_ACCENT = {
  Tirzepatide: "bg-teal-50 text-teal-700 border-teal-200",
  Semaglutide: "bg-pink-50 text-pink-800 border-pink-200",
  Other:       "bg-slate-50 text-slate-700 border-slate-200",
};

export default function PatientOrders() {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [filename, setFilename] = useState("");
  const [drugFilter, setDrugFilter] = useState("all");     // all | Tirzepatide | Semaglutide | ...
  const [bucketFilter, setBucketFilter] = useState("all"); // all | "0-2.5" | "2.5-5" | ...
  const [multiOnly, setMultiOnly] = useState(false);
  const [vialFilter, setVialFilter] = useState("all"); // all | "1" | "2" | "3"
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [sortKey, setSortKey] = useState("default");
  const [sortDir, setSortDir] = useState("desc");
  const [emailTo, setEmailTo] = useState("");
  const [emailFormat, setEmailFormat] = useState("pdf"); // pdf | csv | both
  const [showEmail, setShowEmail] = useState(false);
  const [savedRecipients, setSavedRecipients] = useState([]);
  const [checkedSaved, setCheckedSaved] = useState({}); // { id: true }
  const [newRecipName, setNewRecipName] = useState("");
  const [newRecipEmail, setNewRecipEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoicePricing, setInvoicePricing] = useState({}); // single-month only: { "Semaglutide|0.25": 39 }
  const [invoiceBundles, setInvoiceBundles] = useState([]); // [{family, months, doses:[...], unit_price, label}]
  const [showPricing, setShowPricing] = useState(false);
  const [pricingDraft, setPricingDraft] = useState({}); // same shape as invoicePricing
  const [bundlesDraft, setBundlesDraft] = useState([]); // same shape as invoiceBundles
  const [addingBundle, setAddingBundle] = useState(null); // {family, months, doses:[], unit_price, label}
  const [expandedMissingCombo, setExpandedMissingCombo] = useState(null); // "family|months|doses" key
  const [printingWorksheet, setPrintingWorksheet] = useState(false);
  const [invoiceBillTo, setInvoiceBillTo] = useState({ name: "", address: "", phone: "", email: "" });
  const [invoiceMeta, setInvoiceMeta] = useState({ invoice_number: "", date: "", customer_id: "", terms: "NET 15", tax_rate: 0 });
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [savingToVera, setSavingToVera] = useState(false);
  const [savedReports, setSavedReports] = useState([]);
  const [showSavedList, setShowSavedList] = useState(false);
  const fileRef = useRef();

  const currentPreset = useMemo(() => ({
    drug_filter: drugFilter, bucket_filter: bucketFilter,
    multi_only: multiOnly, search: q,
    sort_key: sortKey, sort_dir: sortDir,
  }), [drugFilter, bucketFilter, multiOnly, q, sortKey, sortDir]);

  const applyPreset = (p) => {
    setDrugFilter(p.drug_filter || "all");
    setBucketFilter(p.bucket_filter || "all");
    setMultiOnly(!!p.multi_only);
    setQ(p.search || "");
    setSortKey(p.sort_key || "default");
    setSortDir(p.sort_dir || "desc");
  };

  const reloadPresets = async () => {
    try {
      const { data } = await api.get("/patient_orders_preset");
      setPresets(data || []);
      const def = (data || []).find((p) => p.is_default);
      return def;
    } catch { return null; }
  };

  useEffect(() => { reloadPresets(); }, []);

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) return toast.error("Give the preset a name first");
    try {
      const existing = presets.find((p) => (p.name || "").toLowerCase() === name.toLowerCase());
      if (existing) await api.put(`/patient_orders_preset/${existing.id}`, { name, ...currentPreset, is_default: !!existing.is_default });
      else          await api.post("/patient_orders_preset", { name, ...currentPreset, is_default: presets.length === 0 });
      toast.success(`Preset "${name}" saved`);
      setPresetName("");
      reloadPresets();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const setDefault = async (p) => {
    try {
      await Promise.all(presets.map((x) =>
        api.put(`/patient_orders_preset/${x.id}`, { ...x, is_default: x.id === p.id })));
      toast.success(`Default preset: ${p.name}`);
      reloadPresets();
    } catch { toast.error("Failed to set default"); }
  };

  const deletePreset = async (p) => {
    try { await api.delete(`/patient_orders_preset/${p.id}`); toast.success(`Deleted "${p.name}"`); reloadPresets(); }
    catch { toast.error("Delete failed"); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setFilename(file.name);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/patient-orders/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport(data);
      // Auto-apply default preset (if any) so the report opens the same way every time
      const def = await reloadPresets();
      if (def) { applyPreset(def); toast.success(`${data.count} rows · applied preset "${def.name}"`); }
      else if (data.warnings?.length) toast.warning(data.warnings.join(" · "));
      else toast.success(`${data.count} order lines · ${data.totals.patients} patients · ${data.totals.multi_vial} multi-vial`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse file");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clear = () => { setReport(null); setFilename(""); setQ(""); setDrugFilter("all"); setBucketFilter("all"); setMultiOnly(false); setVialFilter("all"); };

  const filtered = useMemo(() => {
    let rows = (report?.rows || []).filter((r) => {
      if (drugFilter !== "all" && r.drug_family !== drugFilter) return false;
      if (bucketFilter !== "all" && r.dose_bucket !== bucketFilter) return false;
      if (multiOnly && !r.is_multi_vial) return false;
      if (vialFilter !== "all") {
        const c = Number(r.drug_line_count || 1);
        if (vialFilter === "3" ? c < 3 : String(c) !== vialFilter) return false;
      }
      const n = q.trim().toLowerCase();
      if (n && !(r.patient.toLowerCase().includes(n) || r.drug.toLowerCase().includes(n))) return false;
      return true;
    });
    // Always keep drug families grouped (Semaglutide first, then Tirzepatide, then Other)
    // — user's chosen sort column is applied SECONDARY inside each family group so
    // Sema stays with Sema and Tirz stays with Tirz no matter what column they click.
    const FAM_ORDER = { Semaglutide: 0, Tirzepatide: 1, Other: 9 };
    const cmpFam = (a, b) => (FAM_ORDER[a.drug_family] ?? 9) - (FAM_ORDER[b.drug_family] ?? 9);
    const secondary = (a, b) => {
      const key = sortKey === "default" ? "patient_last" : sortKey;
      const mult = sortKey === "default" ? 1 : (sortDir === "asc" ? 1 : -1);
      const av = a[key], bv = b[key];
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * mult;
      return String(av || "").localeCompare(String(bv || "")) * mult;
    };
    const drugTie = (a, b) => String(a.drug || "").localeCompare(String(b.drug || ""));
    rows = [...rows].sort((a, b) => cmpFam(a, b) || secondary(a, b) || drugTie(a, b));
    return rows;
  }, [report, drugFilter, bucketFilter, multiOnly, vialFilter, q, sortKey, sortDir]);

  // Aggregate multi-month counts per family for the top "supply length" summary
  // card. 1-vial = single-month · 2-vial = 2-month supply · 3+ vial = 3-month.
  const supplyBreakdown = useMemo(() => {
    const rows = report?.rows || [];
    const byFam = {};
    for (const r of rows) {
      const fam = r.drug_family || "Other";
      const c = Math.max(1, Math.min(3, Number(r.drug_line_count || 1)));
      const f = byFam[fam] || { family: fam, one: 0, two: 0, three: 0, total: 0,
                                 patients: new Set() };
      if (c === 1) f.one += 1;
      else if (c === 2) f.two += 1;
      else f.three += 1;
      f.total += 1;
      if (r.patient) f.patients.add(r.patient);
      byFam[fam] = f;
    }
    const famOrder = { Semaglutide: 0, Tirzepatide: 1, Other: 9 };
    return Object.values(byFam)
      .map((f) => ({ ...f, patients: f.patients.size }))
      .sort((a, b) => (famOrder[a.family] ?? 9) - (famOrder[b.family] ?? 9));
  }, [report]);

  const doseBreakdownByCohort = useMemo(() => {
    const rows = report?.rows || [];
    const famOrder = { Semaglutide: 0, Tirzepatide: 1, Other: 9 };

    // Single-month cohort: group by (family, dose)
    const singleBuckets = new Map();
    // Multi-vial cohorts: group by (patient, family, order-group). The order-
    // group discriminator prevents Vera from lumping two separate-day orders
    // (e.g. Stephen Meyerowitz's Jan + Feb 1mg shipments) into a fake bundle.
    const patientCombos = new Map();
    for (const r of rows) {
      const fam = r.drug_family || "Other";
      const dose = Number(r.dose_per_injection_mg || 0);
      let months = Number(r.drug_line_count || 1);
      if (![1, 2, 3].includes(months)) months = Math.min(Math.max(months, 1), 3);
      if (months === 1) {
        const key = `${fam}|${dose}`;
        const b = singleBuckets.get(key) || { months: 1, family: fam, dose_mg: dose, orders: 0 };
        b.orders += 1;
        singleBuckets.set(key, b);
      } else {
        const grp = r.order_group_key || "legacy";
        const pk = `${r.patient || ""}|${fam}|${grp}`;
        const c = patientCombos.get(pk) || { patient: r.patient || "", family: fam, months, doses: [] };
        if (dose > 0) c.doses.push(dose);
        patientCombos.set(pk, c);
      }
    }
    const sortByFamDose = (a, b) =>
      (famOrder[a.family] ?? 9) - (famOrder[b.family] ?? 9) || a.dose_mg - b.dose_mg;
    const sortByFamCombo = (a, b) => {
      const f = (famOrder[a.family] ?? 9) - (famOrder[b.family] ?? 9);
      if (f) return f;
      for (let i = 0; i < Math.max(a.doses.length, b.doses.length); i++) {
        const d = (a.doses[i] ?? 0) - (b.doses[i] ?? 0);
        if (d) return d;
      }
      return 0;
    };
    const bucketCombos = (months) => {
      const map = new Map();
      for (const c of patientCombos.values()) {
        if (c.months !== months) continue;
        const doses = [...c.doses].sort((a, b) => a - b);
        const key = `${c.family}|${doses.join(",")}`;
        const b = map.get(key) || { months, family: c.family, doses, patients: 0, patient_names: [] };
        b.patients += 1;
        if (c.patient) b.patient_names.push(c.patient);
        map.set(key, b);
      }
      // Alphabetise patient names inside each combo bucket so the expanded
      // list + printed worksheet render in a predictable, human-scannable order.
      for (const b of map.values()) b.patient_names.sort((a, b) => a.localeCompare(b));
      return [...map.values()].sort(sortByFamCombo);
    };

    return {
      single: [...singleBuckets.values()].sort(sortByFamDose),
      two: bucketCombos(2),
      three: bucketCombos(3),
    };
  }, [report]);

  // Flat single-dose list — kept for the Generate Invoice's pricing payload
  // builder which needs one row per (family, dose) across all cohorts.
  const doseBreakdown = useMemo(() => {
    const combined = new Map();
    const push = (fam, dose, count) => {
      const key = `${fam}|${dose}`;
      const acc = combined.get(key) || { family: fam, dose_mg: dose, orders: 0 };
      acc.orders += count;
      combined.set(key, acc);
    };
    for (const b of doseBreakdownByCohort.single) push(b.family, b.dose_mg, b.orders);
    for (const c of [...doseBreakdownByCohort.two, ...doseBreakdownByCohort.three]) {
      for (const d of c.doses) push(c.family, d, c.patients);
    }
    const famOrder = { Semaglutide: 0, Tirzepatide: 1, Other: 9 };
    return [...combined.values()].sort((a, b) =>
      (famOrder[a.family] ?? 9) - (famOrder[b.family] ?? 9) || a.dose_mg - b.dose_mg
    );
  }, [doseBreakdownByCohort]);

  const clickSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
  };
  const sortIcon = (k) => sortKey !== k ? "" : (sortDir === "asc" ? " ↑" : " ↓");

  const download = async () => {
    if (!filtered.length) return;
    try {
      const res = await api.post("/patient-orders/export.csv", { rows: filtered },
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `vera-patient-dose-${drugFilter === "all" ? "all" : drugFilter.toLowerCase()}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      toast.success(`CSV downloaded (${filtered.length} rows)`);
    } catch { toast.error("Export failed"); }
  };

  const downloadPdf = async (subset = "all") => {
    if (!filtered.length) return;
    // Filter the payload on the frontend for the button labels' expected counts,
    // but the backend also honours `subset` as a defense-in-depth cross-check.
    const rows = subset === "single_month"
      ? filtered.filter((r) => Number(r.drug_line_count || 1) <= 1)
      : subset === "multi_month"
        ? filtered.filter((r) => Number(r.drug_line_count || 1) >= 2)
        : filtered;
    if (!rows.length) return toast.error(
      subset === "single_month" ? "No 1-month patients in the current filter"
      : subset === "multi_month" ? "No multi-month patients in the current filter"
      : "No rows"
    );
    try {
      const active = presets.find((p) => p.is_default);
      const res = await api.post("/patient-orders/export.pdf",
        { rows, preset_name: active?.name || "", subset }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      const suffix = subset === "single_month" ? "-single-month" : subset === "multi_month" ? "-multi-month" : "";
      link.href = url; link.download = `vera-patient-dose-report${suffix}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      toast.success(`PDF downloaded (${rows.length} rows)`);
    } catch { toast.error("PDF export failed"); }
  };

  const downloadXlsx = async (subset = "all") => {
    if (!filtered.length) return;
    const rows = subset === "single_month"
      ? filtered.filter((r) => Number(r.drug_line_count || 1) <= 1)
      : subset === "multi_month"
        ? filtered.filter((r) => Number(r.drug_line_count || 1) >= 2)
        : filtered;
    if (!rows.length) return toast.error("No rows");
    try {
      const active = presets.find((p) => p.is_default);
      const res = await api.post("/patient-orders/export.xlsx",
        { rows, preset_name: active?.name || "", subset }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const link = document.createElement("a");
      link.href = url; link.download = `vera-patient-dose-report.xlsx`;
      document.body.appendChild(link); link.click(); link.remove();
      toast.success(`Excel downloaded (${rows.length} rows)`);
    } catch { toast.error("Excel export failed"); }
  };

  const emailReport = async () => {
    const typed = emailTo.trim();
    const savedIds = Object.entries(checkedSaved).filter(([, v]) => v).map(([k]) => k);
    if (!typed && savedIds.length === 0) return toast.error("Add at least one recipient");
    setSendingEmail(true);
    try {
      const active = presets.find((p) => p.is_default);
      const { data } = await api.post("/patient-orders/email", {
        rows: filtered, to: typed, saved_ids: savedIds,
        preset_name: active?.name || "", format: emailFormat,
      });
      toast.success(`Sent ${data.rows} rows to ${data.sent} recipient${data.sent === 1 ? "" : "s"} · ${data.format.toUpperCase()}`);
      setShowEmail(false); setEmailTo(""); setCheckedSaved({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    } finally { setSendingEmail(false); }
  };

  const reloadRecipients = async () => {
    try {
      const { data } = await api.get("/patient-orders/recipients");
      setSavedRecipients(data.recipients || []);
    } catch (e) { console.warn("recipients load failed", e); }
  };
  useEffect(() => { if (showEmail) reloadRecipients(); }, [showEmail]);

  const loadPricing = async () => {
    try {
      const { data } = await api.get("/patient-orders/pricing");
      const map = {};
      (data.pricing || []).forEach((p) => {
        map[`${p.family}|${p.dose_mg}`] = Number(p.unit_price || 0);
      });
      setInvoicePricing(map);
      setInvoiceBundles(data.bundles || []);
      const def = data.invoice_defaults || {};
      if (def.bill_to) setInvoiceBillTo((prev) => ({ ...prev, ...def.bill_to }));
      setInvoiceMeta((prev) => ({
        ...prev,
        terms: def.terms || prev.terms,
        tax_rate: def.tax_rate != null ? def.tax_rate : prev.tax_rate,
      }));
    } catch (e) { console.warn("pricing load failed", e); }
  };
  useEffect(() => { loadPricing(); }, []);

  const openInvoice = () => {
    // Pre-fill date with today
    if (!invoiceMeta.date) {
      const t = new Date();
      const d = `${t.getMonth() + 1}/${t.getDate()}/${t.getFullYear()}`;
      setInvoiceMeta((prev) => ({ ...prev, date: d }));
    }
    setShowInvoice(true);
  };

  const priceFor = (family, dose_mg) =>
    Number(invoicePricing[`${family}|${dose_mg}`] || 0);

  // Look up bundle price by (family, months, sorted doses list). Returns null
  // if the combo isn't priced — invoice will show "CUSTOM" and the admin fills
  // it in manually.
  const bundleFor = (family, months, doses) => {
    const sorted = [...doses].sort((a, b) => a - b);
    const key = sorted.map((d) => `${d}`).join(",");
    return (invoiceBundles || []).find(
      (b) => b.family === family && Number(b.months) === Number(months) &&
             [...(b.doses || [])].sort((a, c) => a - c).map((d) => `${d}`).join(",") === key
    ) || null;
  };

  // Unpriced multi-vial combos in the current report — surfaces at the top of
  // the page so the admin can set a price and re-run before printing/emailing.
  // A combo is "missing" if the bundle table has no entry OR the entry has $0.
  const missingCombos = useMemo(() => {
    const out = [];
    for (const cohort of [{ key: "two", months: 2 }, { key: "three", months: 3 }]) {
      for (const row of (doseBreakdownByCohort[cohort.key] || [])) {
        const b = bundleFor(row.family, cohort.months, row.doses);
        const price = Number(b?.unit_price || 0);
        if (!b || price <= 0) {
          out.push({
            family: row.family,
            months: cohort.months,
            doses: row.doses,
            patients: row.patients,
            patient_names: row.patient_names || [],
            existing: !!b,
          });
        }
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doseBreakdownByCohort, invoiceBundles]);

  const openSetPricing = () => {
    setPricingDraft({ ...invoicePricing });
    setBundlesDraft(invoiceBundles.map((b) => ({ ...b, doses: [...b.doses] })));
    setAddingBundle(null);
    setShowPricing(true);
  };

  // Bundle add/delete helpers — admin can extend the default table with any
  // dose combo the pharmacy prices manually (e.g. gap-titration bundles like
  // Sema .5/1 that aren't in the pharmacy sheet by default).
  const familyDoses = (family) => (
    [...new Set(Object.keys(pricingDraft)
      .filter((k) => k.startsWith(`${family}|`))
      .map((k) => Number(k.split("|")[1])))]
      .filter((d) => d > 0)
      .sort((a, b) => a - b)
  );

  const isDefaultBundle = (b) => {
    const doses = [...(b?.doses || [])].sort((a, c) => a - c);
    return DEFAULT_COMBO_KEYS.has(`${b.family}|${Number(b.months)}|${doses.join(",")}`);
  };

  const openAddBundle = (family, months) => {
    setAddingBundle({ family, months, doses: Array(months).fill(""), unit_price: "", label: "" });
  };

  // One-tap "set price for this missing combo" — opens Set Pricing dialog with
  // the add-combo form pre-filled with the combo's family, months, and doses.
  // Admin only needs to type the price (and optionally rename the label) and
  // click Add + Save Pricing to unblock the report.
  const setPriceForMissingCombo = (combo) => {
    setPricingDraft({ ...invoicePricing });
    setBundlesDraft(invoiceBundles.map((b) => ({ ...b, doses: [...b.doses] })));
    const doseStrs = combo.doses.map((d) => String(d));
    const suggestedLabel = `${combo.family} ${combo.months === 2 ? "60" : "90"}-Day Bundle (` +
      combo.doses.map((d) => `${d}mg`).join("/") + ")";
    setAddingBundle({
      family: combo.family,
      months: combo.months,
      doses: doseStrs,
      unit_price: "",
      label: suggestedLabel,
    });
    setShowPricing(true);
  };

  // Download a single-PDF worksheet listing every unpriced combo (with the
  // patient names + blank price line) so the admin can print, take to the
  // pricing meeting, and come back to enter the numbers in Set Pricing.
  const printMissingCombosWorksheet = async () => {
    if (!missingCombos.length) return;
    setPrintingWorksheet(true);
    try {
      const res = await api.post("/patient-orders/missing-combos.pdf",
        { combos: missingCombos.map((c) => ({
            family: c.family, months: c.months, doses: c.doses,
            patients: c.patients, patient_names: c.patient_names || [],
            existing: !!c.existing,
          })) },
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "vera-bundle-pricing-worksheet.pdf";
      document.body.appendChild(link); link.click(); link.remove();
      toast.success(`Worksheet downloaded (${missingCombos.length} combos)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Worksheet generation failed");
    } finally { setPrintingWorksheet(false); }
  };

  const setAddDose = (slot, val) => {
    setAddingBundle((prev) => {
      if (!prev) return prev;
      const doses = [...prev.doses];
      doses[slot] = val;
      return { ...prev, doses };
    });
  };

  const confirmAddBundle = () => {
    const b = addingBundle;
    if (!b) return;
    const doses = (b.doses || []).map(Number).filter((d) => d > 0);
    if (doses.length !== Number(b.months)) {
      return toast.error(`Pick ${b.months} dose${b.months > 1 ? "s" : ""} for this bundle`);
    }
    const sorted = [...doses].sort((a, b) => a - b);
    const key = `${b.family}|${Number(b.months)}|${sorted.join(",")}`;
    const dupe = (bundlesDraft || []).some((x) => (
      x.family === b.family && Number(x.months) === Number(b.months) &&
      [...(x.doses || [])].sort((a, c) => a - c).join(",") === sorted.join(",")
    ));
    if (dupe) return toast.error("That combo already exists in the table");
    const price = Number(b.unit_price || 0);
    if (price <= 0) return toast.error("Enter a bundle price greater than $0");
    setBundlesDraft((prev) => [...prev, {
      family: b.family, months: Number(b.months), doses: sorted,
      unit_price: price, label: (b.label || "").trim(),
    }]);
    setAddingBundle(null);
    toast.success(`Added ${b.family} ${b.months}-mo ${sorted.map((d) => `${d}mg`).join("/")} · click Save Pricing to persist`);
  };

  const removeBundle = (b) => {
    if (isDefaultBundle(b)) {
      return toast.error("Default combos can't be removed — they'll be restored on next load. Edit the price instead.");
    }
    const doses = [...(b.doses || [])].sort((a, c) => a - c);
    if (!window.confirm(`Remove ${b.family} ${b.months}-mo bundle (${doses.map((d) => `${d}mg`).join("/")})?`)) return;
    setBundlesDraft((prev) => prev.filter((x) => !(
      x.family === b.family && Number(x.months) === Number(b.months) &&
      [...(x.doses || [])].sort((a, c) => a - c).join(",") === doses.join(",")
    )));
  };

  const savePricingDraft = async () => {
    // Single-month rows
    const singleRows = [];
    Object.entries(pricingDraft).forEach(([k, v]) => {
      const [family, doseStr] = k.split("|");
      const dose_mg = Number(doseStr);
      if (!family || !dose_mg) return;
      singleRows.push({ family, dose_mg, months: 1, unit_price: Number(v || 0) });
    });
    // Bundle rows
    const bundleRows = (bundlesDraft || []).map((b) => ({
      family: b.family,
      months: Number(b.months),
      doses: [...(b.doses || [])].map(Number),
      unit_price: Number(b.unit_price || 0),
      label: b.label || "",
    }));
    try {
      await api.put("/patient-orders/pricing", { pricing: singleRows, bundles: bundleRows });
      toast.success("Pricing saved");
      setShowPricing(false);
      await loadPricing();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const resetPricingToDefaults = async () => {
    if (!window.confirm(
      "Reset all dose prices to the Amended Exhibit A pharmacy defaults?\n\n" +
      "SINGLE-MONTH\n" +
      "Sema  .25/$39  .5/$41  1/$44  1.5/$47  2/$49  2.4/$62\n" +
      "Tirz  2.5/$67  5/$69  7.5/$72  10/$92  12.5/$97  15/$104\n\n" +
      "2-MONTH BUNDLES (dose combos)\n" +
      "Sema  .25/.5=$69  1/1.5=$79  2/2.4=$99  2.4/2.4=$119\n" +
      "Tirz  2.5/5=$129  5/7.5=$149  10/12.5=$169  15/15=$189\n\n" +
      "3-MONTH BUNDLES\n" +
      "Sema  .25/.5/1=$99  1.5/2/2.4=$129  2.4/2.4/2.4=$159\n" +
      "Tirz  2.5/5/7.5=$179  10/12.5/15=$239  15/15/15=$259\n\n" +
      "Your current pricing overrides will be replaced."
    )) return;
    try {
      const { data } = await api.post("/patient-orders/pricing/reset");
      toast.success(`Reset ${data.count} pricing rows to defaults`);
      await loadPricing();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  const invoiceLines = useMemo(() => {    // Build invoice lines that mirror the backend's PDF layout:
    //   • one line per (family, dose) for 1-vial patients
    //   • one line per (family, dose-combo) for 2-vial and 3-vial patients
    // Multi-vial rows are priced from the bundle table; missing combos show CUSTOM.
    const lines = [];
    const famOrder = { Semaglutide: 0, Tirzepatide: 1, Other: 9 };

    // Single-month: group by (family, dose)
    const bySingle = new Map();
    // Multi-vial: group per (patient, family, order-group) so two separate-day
    // orders don't collapse into a fake bundle.
    const byPatient = new Map();
    for (const r of (report?.rows || [])) {
      const fam = r.drug_family || "Other";
      const dose = Number(r.dose_per_injection_mg || 0);
      if (!dose) continue;
      let months = Number(r.drug_line_count || 1);
      if (![1, 2, 3].includes(months)) months = Math.min(Math.max(months, 1), 3);
      if (months === 1) {
        const key = `${fam}|${dose}`;
        const b = bySingle.get(key) || { family: fam, dose_mg: dose, months: 1, orders: 0 };
        b.orders += 1;
        bySingle.set(key, b);
      } else {
        const grp = r.order_group_key || "legacy";
        const pk = `${r.patient || ""}|${fam}|${grp}`;
        const c = byPatient.get(pk) || { patient: r.patient, family: fam, months, doses: [] };
        c.doses.push(dose);
        byPatient.set(pk, c);
      }
    }

    // Single lines
    for (const b of [...bySingle.values()].sort((a, b) =>
        (famOrder[a.family] ?? 9) - (famOrder[b.family] ?? 9) || a.dose_mg - b.dose_mg)) {
      const price = priceFor(b.family, b.dose_mg);
      lines.push({ kind: "single", family: b.family, dose_mg: b.dose_mg, months: 1,
                   orders: b.orders, unit_price: price, amount: price * b.orders,
                   custom: false });
    }

    // Combo lines
    const byCombo = new Map();
    for (const c of byPatient.values()) {
      const doses = [...c.doses].sort((a, b) => a - b);
      const key = `${c.family}|${c.months}|${doses.join(",")}`;
      const b = byCombo.get(key) || { family: c.family, months: c.months, doses, patients: 0 };
      b.patients += 1;
      byCombo.set(key, b);
    }
    const sortByFamCombo = (a, b) => {
      const f = (famOrder[a.family] ?? 9) - (famOrder[b.family] ?? 9);
      if (f) return f;
      if (a.months !== b.months) return a.months - b.months;
      for (let i = 0; i < Math.max(a.doses.length, b.doses.length); i++) {
        const d = (a.doses[i] ?? 0) - (b.doses[i] ?? 0);
        if (d) return d;
      }
      return 0;
    };
    for (const b of [...byCombo.values()].sort(sortByFamCombo)) {
      const bundle = bundleFor(b.family, b.months, b.doses);
      const price = Number(bundle?.unit_price || 0);
      const label = bundle?.label || (
        `${b.family} ` + b.doses.map((d) => `${d}mg`).join("/") + ` · ${b.months}-mo`
      );
      lines.push({
        kind: "bundle", family: b.family, months: b.months, doses: b.doses,
        orders: b.patients, unit_price: price, amount: price * b.patients,
        label, custom: !bundle,
      });
    }
    return lines;
  }, [report, invoicePricing, invoiceBundles]);

  const invoiceSubtotal = useMemo(() => invoiceLines.reduce((s, l) => s + l.amount, 0), [invoiceLines]);
  const invoiceTax = useMemo(() => invoiceSubtotal * (Number(invoiceMeta.tax_rate || 0) / 100), [invoiceSubtotal, invoiceMeta.tax_rate]);
  const invoiceTotal = invoiceSubtotal + invoiceTax;

  const loadSavedReports = async () => {
    try {
      const { data } = await api.get("/patient-orders/saved");
      setSavedReports(data.reports || []);
    } catch (e) { console.warn("saved reports load failed", e); }
  };
  useEffect(() => { loadSavedReports(); }, []);

  const saveToVera = async () => {
    if (!filtered.length) return toast.error("No orders in the current filter");
    setSavingToVera(true);
    try {
      await api.post("/patient-orders/save", {
        rows: filtered,
        filename,
        preset_name: activePreset?.name || "",
      });
      toast.success("Saved to Vera — pull it back up from Saved reports anytime");
      await loadSavedReports();
      setShowSavedList(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSavingToVera(false); }
  };

  const downloadSaved = async (id, savedAt) => {
    try {
      const res = await api.get(`/patient-orders/saved/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `vera-dose-report-${(savedAt || "").slice(0, 10)}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const deleteSaved = async (id) => {
    if (!confirm("Delete this saved report? This cannot be undone.")) return;
    try {
      await api.delete(`/patient-orders/saved/${id}`);
      toast.success("Saved report deleted");
      setSavedReports((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const activePreset = presets.find((p) => p.is_default);

  const generateInvoice = async () => {
    if (!filtered.length) return toast.error("No orders in the current filter");
    if (!invoiceBillTo.name.trim()) return toast.error("Bill To — clinic name is required");
    setGeneratingInvoice(true);
    try {
      // Send BOTH single-month prices AND bundle prices so the backend prices
      // each patient by their supply length correctly.
      const pricingArr = doseBreakdown
        .filter((b) => b.dose_mg > 0)
        .map((b) => ({ family: b.family, dose_mg: b.dose_mg,
                       unit_price: priceFor(b.family, b.dose_mg) }));
      const bundlesArr = (invoiceBundles || []).map((b) => ({
        family: b.family, months: Number(b.months),
        doses: [...(b.doses || [])].map(Number),
        unit_price: Number(b.unit_price || 0),
        label: b.label || "",
      }));
      const res = await api.post("/patient-orders/invoice.pdf",
        { rows: filtered, bill_to: invoiceBillTo, pricing: pricingArr, bundles: bundlesArr,
          ...invoiceMeta, tax_rate: Number(invoiceMeta.tax_rate || 0) },
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const slug = (invoiceBillTo.name || "clinic").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      link.download = `vera-invoice-${slug}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("Invoice PDF generated · pricing saved");
      setShowInvoice(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invoice generation failed");
    } finally { setGeneratingInvoice(false); }
  };

  const addRecipient = async () => {
    const email = newRecipEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) return toast.error("Enter a valid email");
    try {
      await api.post("/patient-orders/recipients", { name: newRecipName.trim(), email });
      toast.success(`Added ${email} to distribution list`);
      setNewRecipName(""); setNewRecipEmail("");
      reloadRecipients();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add recipient"); }
  };

  const removeRecipient = async (r) => {
    try {
      await api.delete(`/patient-orders/recipients/${r.id}`);
      toast.success(`Removed ${r.email}`);
      setCheckedSaved((prev) => { const c = { ...prev }; delete c[r.id]; return c; });
      reloadRecipients();
    } catch { toast.error("Could not remove recipient"); }
  };

  return (
    <div>
      <PageHeader testid="patient-orders-header" title="Patient Dose Report"
        subtitle="Upload the PMS order export. Vera collapses multi-vial orders, splits by drug family, groups doses into standard buckets, and separates multi-month buyers into their own filter." />

      <Card className="p-5 border-slate-200 mb-6">
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={upload} />
        {!report ? (
          <button data-testid="patient-orders-upload-btn" onClick={() => fileRef.current?.click()} disabled={busy}
            className="w-full p-10 border-2 border-dashed border-pink-200 rounded-xl hover:border-pink-700 hover:bg-pink-50/30 transition-colors flex flex-col items-center gap-3 disabled:opacity-60">
            {busy
              ? <><Loader2 className="w-8 h-8 text-pink-700 animate-spin" /><span className="text-sm text-slate-500">Parsing {filename}…</span></>
              : <><UploadCloud className="w-8 h-8 text-pink-700" />
                  <div className="text-center">
                    <div className="font-semibold text-slate-800">Upload PMS Export</div>
                    <div className="text-xs text-slate-500 mt-1">Drop a .csv or .xlsx file — Vera generates a per-patient dose report grouped by drug and titration bucket.</div>
                  </div></>}
          </button>
        ) : (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-pink-700" />
              <div>
                <div className="font-semibold text-slate-800">
                  {report.totals.patients} patient{report.totals.patients === 1 ? "" : "s"} in report · {report.count} order lines
                </div>
                <div className="text-xs text-slate-500 font-mono">{filename}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 text-xs items-center">
              <div><span className="text-slate-500">Total vials</span> <span className="font-mono font-semibold text-slate-900 ml-1">{num(report.totals.vials)}</span></div>
              <div><span className="text-slate-500">Weekly mg</span> <span className="font-mono font-semibold text-pink-800 ml-1">{num(report.totals.weekly_mg)}</span></div>
              <div><span className="text-slate-500">Monthly mg</span> <span className="font-mono font-semibold text-pink-800 ml-1">{num(report.totals.monthly_mg)}</span></div>
              <Button data-testid="patient-orders-download" onClick={download} className="bg-pink-700 hover:bg-pink-800 gap-1.5 h-9">
                <Download className="w-4 h-4" /> CSV
              </Button>
              <Button data-testid="patient-orders-download-xlsx" onClick={() => downloadXlsx("all")}
                variant="outline" className="gap-1.5 h-9 border-green-600 text-green-800 hover:bg-green-50"
                title="Formatted Excel workbook — breakdown, bundles & order detail on separate sheets">
                <Download className="w-4 h-4" /> Excel
              </Button>
              <Button data-testid="patient-orders-download-pdf" onClick={() => downloadPdf("all")} variant="outline" className="gap-1.5 h-9">
                <Download className="w-4 h-4" /> PDF
              </Button>
              <Button data-testid="patient-orders-download-pdf-single" onClick={() => downloadPdf("single_month")}
                variant="outline" className="gap-1.5 h-9 border-emerald-500 text-emerald-800 hover:bg-emerald-50"
                title="PDF of only 1-month (single vial) patients">
                <Download className="w-4 h-4" /> 1-mo PDF
              </Button>
              <Button data-testid="patient-orders-download-pdf-multi" onClick={() => downloadPdf("multi_month")}
                variant="outline" className="gap-1.5 h-9 border-rose-500 text-rose-800 hover:bg-rose-50"
                title="PDF of only 2 and 3-month (multi-vial) patients">
                <Download className="w-4 h-4" /> Multi-mo PDF
              </Button>
              <Button data-testid="patient-orders-set-pricing" onClick={openSetPricing} variant="outline" className="gap-1.5 h-9 border-amber-500 text-amber-800 hover:bg-amber-50">
                <DollarSign className="w-4 h-4" /> Set Pricing
              </Button>
              <Button data-testid="patient-orders-invoice-btn" onClick={openInvoice} variant="outline" className="gap-1.5 h-9 border-blue-800 text-blue-800 hover:bg-blue-50">
                <FileText className="w-4 h-4" /> Invoice
              </Button>
              <Button data-testid="patient-orders-save-to-vera" onClick={saveToVera} disabled={savingToVera} variant="outline" className="gap-1.5 h-9 border-emerald-700 text-emerald-800 hover:bg-emerald-50">
                {savingToVera ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                Save to Vera
              </Button>
              <Button data-testid="patient-orders-email-btn" onClick={() => setShowEmail((s) => !s)} variant="outline" className="gap-1.5 h-9">
                <Mail className="w-4 h-4" /> Email
              </Button>
              <Button data-testid="patient-orders-clear" variant="outline" onClick={clear} className="gap-1.5 h-9">
                <X className="w-4 h-4" /> Clear
              </Button>
            </div>
          </div>
        )}
      </Card>

      {report && missingCombos.length > 0 && (
        <Card data-testid="missing-combos-banner"
              className="p-4 mb-4 border-2 border-amber-400 bg-amber-50/70">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="text-sm font-heading font-bold text-amber-900">
                  {missingCombos.length} bundle combo{missingCombos.length === 1 ? "" : "s"} in this report {missingCombos.length === 1 ? "has" : "have"} no price yet
                </h3>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900 border border-amber-400">
                  ACTION NEEDED
                </span>
                <Button
                  data-testid="missing-combos-print-worksheet"
                  size="sm"
                  variant="outline"
                  onClick={printMissingCombosWorksheet}
                  disabled={printingWorksheet}
                  className="ml-auto h-7 gap-1.5 text-xs border-amber-400 text-amber-900 hover:bg-amber-100"
                  title="Download a printable pricing worksheet listing every combo + patient names + blank price lines"
                >
                  {printingWorksheet ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                  Print pricing worksheet
                </Button>
              </div>
              <p className="text-xs text-amber-800 mb-3">
                These titration combos aren't in the Amended Exhibit A schedule. Tap <b>Set price</b> on
                any row to open the pricing dialog with the combo pre-filled — enter the price, save,
                and the report totals will refresh automatically. Tap <b>N patient(s) affected</b>
                to see who's on each combo.
              </p>
              <div className="space-y-1.5">
                {missingCombos.map((c, i) => {
                  const key = `${c.family}|${c.months}|${c.doses.join(",")}`;
                  const isOpen = expandedMissingCombo === key;
                  return (
                  <div key={`missing-${key}`}
                    data-testid={`missing-combo-row-${i}`}
                    className="bg-white/70 rounded-md border border-amber-200 overflow-hidden">
                    <div className="flex items-center gap-2 flex-wrap px-3 py-2">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${FAMILY_ACCENT[c.family] || FAMILY_ACCENT.Other}`}>
                        {c.family}
                      </span>
                      <span className="text-[11px] font-mono px-2 py-0.5 rounded-full border bg-slate-100 text-slate-700 border-slate-300">
                        {c.months}-month bundle
                      </span>
                      <span className="font-mono text-sm font-semibold text-slate-800">
                        {c.doses.map((d) => `${d}mg`).join(" / ")}
                      </span>
                      <button
                        type="button"
                        data-testid={`missing-combo-toggle-patients-${i}`}
                        onClick={() => setExpandedMissingCombo(isOpen ? null : key)}
                        className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-amber-900 underline underline-offset-2 decoration-dotted decoration-slate-400 hover:decoration-amber-700"
                        title={isOpen ? "Hide patient list" : "Show affected patient names"}
                      >
                        <span>· {c.patients} patient{c.patients === 1 ? "" : "s"} affected</span>
                        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                      {c.existing && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-300"
                              title="Combo is in the pricing table but priced at $0">
                          listed · $0
                        </span>
                      )}
                      <button
                        type="button"
                        data-testid={`missing-combo-set-price-${i}`}
                        onClick={() => setPriceForMissingCombo(c)}
                        className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-white bg-amber-700 hover:bg-amber-800 px-3 py-1 rounded-md"
                        title="Open Set Pricing pre-filled with this combo"
                      >
                        <DollarSign className="w-3 h-3" /> Set price
                      </button>
                    </div>
                    {isOpen && (
                      <div data-testid={`missing-combo-patients-${i}`}
                           className="border-t border-amber-200 bg-amber-50/40 px-3 py-2">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-800 mb-1">
                          Affected patients ({(c.patient_names || []).length})
                        </div>
                        {(c.patient_names || []).length === 0 ? (
                          <div className="text-xs text-slate-400 italic">No patient names available</div>
                        ) : (
                          <ol className="text-xs text-slate-800 pl-5 list-decimal space-y-0.5">
                            {(c.patient_names || []).map((n, ni) => (
                              <li key={`p-${ni}-${n}`} data-testid={`missing-combo-patient-${i}-${ni}`}
                                  className="font-mono">
                                {n}
                              </li>
                            ))}
                          </ol>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
              <div className="text-[11px] text-amber-700 mt-2 italic">
                Tip: prices you save here persist for future reports — you only have to do this once per combo.
              </div>
            </div>
          </div>
        </Card>
      )}

      {report && (
        <Card className="p-3 mb-4 border-slate-200 flex flex-wrap items-center gap-2" data-testid="preset-bar">
          <Bookmark className="w-4 h-4 text-pink-700" />
          <span className="text-xs text-slate-500 mr-1">Presets:</span>
          {presets.length === 0 ? (
            <span className="text-xs text-slate-400 italic">none saved yet — configure filters then save one below</span>
          ) : presets.map((p) => (
            <div key={p.id} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full border border-pink-200 bg-white text-xs">
              <button data-testid={`preset-apply-${p.id}`} onClick={() => applyPreset(p)}
                className="text-pink-800 hover:underline font-medium">{p.name}</button>
              <button data-testid={`preset-default-${p.id}`} onClick={() => setDefault(p)}
                title={p.is_default ? "Default (auto-applied on upload)" : "Set as default"}
                className={p.is_default ? "text-amber-500" : "text-slate-300 hover:text-amber-500"}>
                <Star className="w-3.5 h-3.5" fill={p.is_default ? "currentColor" : "none"} />
              </button>
              <button data-testid={`preset-delete-${p.id}`} onClick={() => deletePreset(p)}
                className="text-slate-300 hover:text-rose-500"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          <div className="flex items-center gap-1 ml-auto">
            <Input data-testid="preset-name-input" placeholder="Save current filters as…"
              value={presetName} onChange={(e) => setPresetName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && savePreset()}
              className="h-8 text-xs w-56" />
            <Button data-testid="preset-save" onClick={savePreset} size="sm"
              className="bg-pink-700 hover:bg-pink-800 h-8 gap-1.5">
              <Save className="w-3.5 h-3.5" /> Save
            </Button>
          </div>
        </Card>
      )}

      {report?.warnings?.length > 0 && (
        <div className="p-3 mb-4 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><div>{report.warnings.join(" · ")}</div>
        </div>
      )}

      {savedReports.length > 0 && (
        <Card className="p-3 mb-4 border-emerald-200" data-testid="saved-reports-card">
          <button onClick={() => setShowSavedList((s) => !s)}
            data-testid="saved-reports-toggle"
            className="w-full flex items-center gap-2 text-sm font-semibold text-slate-800 hover:text-emerald-800">
            <Archive className="w-4 h-4 text-emerald-700" />
            Saved reports
            <span className="text-xs font-normal text-slate-500">({savedReports.length})</span>
            <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${showSavedList ? "rotate-180" : ""}`} />
          </button>
          {showSavedList && (
            <div className="mt-3 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 text-xs">
                    <TableHead>Saved at</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Preset</TableHead>
                    <TableHead>Saved by</TableHead>
                    <TableHead className="text-right">Patients</TableHead>
                    <TableHead className="text-right">Orders</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {savedReports.map((s) => (
                    <TableRow key={s.id} data-testid={`saved-report-row-${s.id}`}>
                      <TableCell className="text-xs font-mono text-slate-700 whitespace-nowrap">
                        {(s.saved_at || "").replace("T", " ").slice(0, 16)}
                      </TableCell>
                      <TableCell className="text-xs text-slate-800">{s.filename}</TableCell>
                      <TableCell className="text-xs text-slate-600">{s.preset_name || <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="text-xs text-slate-500">{s.saved_by}</TableCell>
                      <TableCell className="text-xs font-mono text-right text-pink-800">{s.patient_count}</TableCell>
                      <TableCell className="text-xs font-mono text-right text-slate-700">{s.order_count}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button data-testid={`saved-report-download-${s.id}`} onClick={() => downloadSaved(s.id, s.saved_at)}
                          size="sm" variant="outline" className="h-7 gap-1 text-xs">
                          <Download className="w-3 h-3" /> PDF
                        </Button>
                        <Button data-testid={`saved-report-delete-${s.id}`} onClick={() => deleteSaved(s.id)}
                          size="sm" variant="ghost" className="h-7 gap-1 text-xs text-rose-600 hover:text-rose-800 hover:bg-rose-50">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      )}

      {showEmail && report && (
        <Card className="p-4 mb-4 border-pink-200 space-y-3" data-testid="email-bar">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Mail className="w-4 h-4 text-pink-700" /> Email dose report
            <span className="ml-auto text-[11px] font-normal text-slate-400">
              {filtered.length} currently-filtered rows
            </span>
          </div>

          {/* Format selector */}
          <div className="flex items-center gap-3 text-xs">
            <span className="text-slate-500">Format:</span>
            {[
              { v: "pdf", label: "PDF only" },
              { v: "csv", label: "CSV only" },
              { v: "both", label: "PDF + CSV" },
            ].map((f) => (
              <label key={f.v} data-testid={`email-format-${f.v}`}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border cursor-pointer ${
                  emailFormat === f.v
                    ? "bg-pink-700 text-white border-pink-700"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}>
                <input type="radio" name="email-format" value={f.v}
                  checked={emailFormat === f.v}
                  onChange={() => setEmailFormat(f.v)}
                  className="hidden" />
                {f.label}
              </label>
            ))}
          </div>

          {/* Type-in recipients */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500 w-28">Type emails:</span>
            <Input data-testid="email-input" type="text"
              placeholder="alice@rx.com, bob@rx.com …"
              value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
              className="h-8 text-xs flex-1 min-w-[240px]" />
            <span className="text-[11px] text-slate-400">Separate multiple with commas.</span>
          </div>

          {/* Saved distribution list */}
          <div className="flex flex-wrap items-start gap-2">
            <span className="text-xs text-slate-500 w-28 pt-1">Saved list:</span>
            <div className="flex-1 min-w-[240px]">
              {savedRecipients.length === 0 ? (
                <span className="text-xs text-slate-400 italic">
                  No saved recipients yet — add teammates below so you can one-click send next time.
                </span>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="saved-recipient-list">
                  {savedRecipients.map((r) => (
                    <label key={r.id} data-testid={`saved-recipient-${r.id}`}
                      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-xs cursor-pointer ${
                        checkedSaved[r.id]
                          ? "bg-pink-50 border-pink-300 text-pink-900"
                          : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                      }`}>
                      <input type="checkbox" checked={!!checkedSaved[r.id]}
                        onChange={(e) => setCheckedSaved((c) => ({ ...c, [r.id]: e.target.checked }))}
                        className="accent-pink-700" />
                      <span className="font-medium">{r.name || r.email.split("@")[0]}</span>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-500 font-mono">{r.email}</span>
                      <button
                        type="button"
                        data-testid={`saved-recipient-remove-${r.id}`}
                        onClick={(e) => { e.preventDefault(); removeRecipient(r); }}
                        className="ml-1 text-slate-300 hover:text-rose-500" title="Remove from list">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </label>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-1.5 mt-2 pt-2 border-t border-slate-100">
                <Input data-testid="saved-recipient-name" value={newRecipName}
                  onChange={(e) => setNewRecipName(e.target.value)}
                  placeholder="Name (optional)" className="h-7 text-xs w-40" />
                <Input data-testid="saved-recipient-email" value={newRecipEmail}
                  onChange={(e) => setNewRecipEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRecipient())}
                  placeholder="teammate@pharmacy.com" className="h-7 text-xs w-56" />
                <Button data-testid="saved-recipient-add" size="sm" variant="outline"
                  onClick={addRecipient} className="h-7 gap-1.5 text-xs">
                  <Plus className="w-3 h-3" /> Save to list
                </Button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
            <Button data-testid="email-send" size="sm" onClick={emailReport}
              disabled={sendingEmail}
              className="bg-pink-700 hover:bg-pink-800 h-8 gap-1.5">
              {sendingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send report
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowEmail(false)} className="h-8">
              Cancel
            </Button>
            <span className="text-[11px] text-slate-400 ml-1">
              Each recipient receives a private email — the “to” list is never shared.
            </span>
          </div>
        </Card>
      )}

      {report && showInvoice && (
        <Card data-testid="invoice-panel" className="p-5 mb-6 border-blue-800 border-2 bg-blue-50/20">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-800" />
              <h3 className="text-base font-bold text-blue-900">Compounding Invoice — for Clinic</h3>
            </div>
            <button data-testid="invoice-close" onClick={() => setShowInvoice(false)} className="text-slate-400 hover:text-slate-700">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div className="space-y-2">
              <div className="text-[11px] font-semibold text-blue-900 uppercase tracking-wider">Bill To</div>
              <Input data-testid="invoice-billto-name" placeholder="Clinic name (e.g. CareValidate)"
                value={invoiceBillTo.name} onChange={(e) => setInvoiceBillTo({ ...invoiceBillTo, name: e.target.value })}
                className="h-9 text-sm" />
              <textarea data-testid="invoice-billto-address" placeholder="Street address, City, State ZIP"
                value={invoiceBillTo.address} onChange={(e) => setInvoiceBillTo({ ...invoiceBillTo, address: e.target.value })}
                rows={2}
                className="w-full text-sm px-3 py-1.5 rounded-md border border-slate-200 focus:border-blue-800 focus:outline-none focus:ring-1 focus:ring-blue-800" />
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="invoice-billto-phone" placeholder="Phone"
                  value={invoiceBillTo.phone} onChange={(e) => setInvoiceBillTo({ ...invoiceBillTo, phone: e.target.value })}
                  className="h-9 text-sm" />
                <Input data-testid="invoice-billto-email" placeholder="Email"
                  value={invoiceBillTo.email} onChange={(e) => setInvoiceBillTo({ ...invoiceBillTo, email: e.target.value })}
                  className="h-9 text-sm" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-[11px] font-semibold text-blue-900 uppercase tracking-wider">Invoice Details</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase">Invoice #</label>
                  <Input data-testid="invoice-number" value={invoiceMeta.invoice_number}
                    onChange={(e) => setInvoiceMeta({ ...invoiceMeta, invoice_number: e.target.value })}
                    placeholder="1-0126" className="h-9 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase">Date</label>
                  <Input data-testid="invoice-date" value={invoiceMeta.date}
                    onChange={(e) => setInvoiceMeta({ ...invoiceMeta, date: e.target.value })}
                    placeholder="MM/DD/YYYY" className="h-9 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase">Customer ID</label>
                  <Input data-testid="invoice-customer-id" value={invoiceMeta.customer_id}
                    onChange={(e) => setInvoiceMeta({ ...invoiceMeta, customer_id: e.target.value })}
                    placeholder="1013" className="h-9 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase">Terms</label>
                  <Input data-testid="invoice-terms" value={invoiceMeta.terms}
                    onChange={(e) => setInvoiceMeta({ ...invoiceMeta, terms: e.target.value })}
                    placeholder="NET 15" className="h-9 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase">Tax rate %</label>
                  <Input data-testid="invoice-tax-rate" type="number" step="0.01"
                    value={invoiceMeta.tax_rate}
                    onChange={(e) => setInvoiceMeta({ ...invoiceMeta, tax_rate: e.target.value })}
                    placeholder="0" className="h-9 text-sm" />
                </div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <div className="text-[11px] font-semibold text-blue-900 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5" /> Unit price per dose — auto-saved on generate
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded-md">
              <Table data-testid="invoice-pricing-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right w-32">Unit Price</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoiceLines.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-slate-400 text-sm py-6">No dose lines in the current filter</TableCell></TableRow>
                  ) : invoiceLines.map((l, i) => {
                    const key = l.kind === "bundle"
                      ? `${l.family}-${l.months}-${l.doses.join(",")}`
                      : `${l.family}-${l.dose_mg}-1`;
                    const desc = l.kind === "bundle"
                      ? l.label
                      : `${l.family} ${l.dose_mg}mg`;
                    return (
                    <TableRow key={key} data-testid={`invoice-line-${i}`}>
                      <TableCell className="text-sm text-slate-800 font-medium">
                        {desc}
                        {l.kind === "bundle" && (
                          <span className="ml-1.5 text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-800 border border-rose-300">
                            {l.months}-mo bundle
                          </span>
                        )}
                        {l.custom && (
                          <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300">
                            CUSTOM — set price
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-right text-slate-700">{l.orders}</TableCell>
                      <TableCell className="text-right">
                        <span data-testid={`invoice-price-${key}`}
                          className="font-mono text-sm text-slate-700">
                          {l.custom ? <span className="text-amber-700">manual</span> : `$${Number(l.unit_price || 0).toFixed(2)}`}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-right font-semibold text-blue-900">
                        {l.custom ? <span className="text-amber-700 text-xs">—</span>
                          : `$${l.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="flex justify-end mb-4">
            <div className="w-72 space-y-1 text-sm">
              <div className="flex justify-between border-t border-blue-800 pt-2">
                <span className="font-semibold text-slate-700">SUBTOTAL</span>
                <span className="font-mono font-semibold" data-testid="invoice-subtotal">${invoiceSubtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>TAX ({Number(invoiceMeta.tax_rate || 0)}%)</span>
                <span className="font-mono" data-testid="invoice-tax">${invoiceTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between bg-blue-800 text-white px-3 py-2 rounded font-bold">
                <span>TOTAL</span>
                <span className="font-mono" data-testid="invoice-total">${invoiceTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <Button data-testid="invoice-generate" onClick={generateInvoice} disabled={generatingInvoice}
              className="bg-blue-800 hover:bg-blue-900 h-9 gap-1.5">
              {generatingInvoice ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Generate invoice PDF
            </Button>
            <Button variant="ghost" onClick={() => setShowInvoice(false)} className="h-9">Cancel</Button>
            <span className="text-[11px] text-slate-400 ml-1">
              PDF matches Polomar-style invoice · pricing + clinic details save automatically
            </span>
          </div>
        </Card>
      )}

      {report && doseBreakdown.length > 0 && (
        <Card data-testid="dose-breakdown-card" className="p-5 mb-6 border-slate-200">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Syringe className="w-4 h-4 text-pink-700" />
            <h3 className="text-sm font-semibold text-slate-800">Compounding breakdown by dose</h3>
            <span className="text-[11px] text-slate-500">
              {report.totals.patients} patient{report.totals.patients === 1 ? "" : "s"} · {report.count} order lines
            </span>
            <div className="ml-auto flex items-center gap-3">
              <button data-testid="pricing-reset-defaults"
                onClick={resetPricingToDefaults}
                className="text-[11px] text-amber-800 hover:text-amber-900 underline font-medium">
                Reset prices to pharmacy defaults
              </button>
              <span className="text-[11px] text-slate-500">
                Edit prices via <button onClick={openSetPricing} className="text-amber-800 hover:underline font-medium">Set Pricing</button>
              </span>
            </div>
          </div>
          {[
            { key: "single", months: 1, label: "1-vial · Single-month", chip: "bg-emerald-100 text-emerald-900 border-emerald-300",
              rowAccent: "border-t-emerald-200", subtotalBg: "bg-emerald-50/60" },
            { key: "two",    months: 2, label: "2-vial · 2-month bundle", chip: "bg-amber-100 text-amber-900 border-amber-300",
              rowAccent: "border-t-amber-200", subtotalBg: "bg-amber-50/60" },
            { key: "three",  months: 3, label: "3-vial · 3-month bundle", chip: "bg-rose-100 text-rose-900 border-rose-300",
              rowAccent: "border-t-rose-200", subtotalBg: "bg-rose-50/60" },
          ].map((cohort) => {
            const rows = doseBreakdownByCohort[cohort.key] || [];
            if (rows.length === 0) return null;
            const isSingle = cohort.months === 1;
            const totalCount = rows.reduce((s, b) => s + (isSingle ? b.orders : b.patients), 0);
            return (
              <div key={cohort.key} data-testid={`dose-breakdown-${cohort.key}`} className="mb-6 last:mb-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cohort.chip}`}>
                    {cohort.label}
                  </span>
                  <span className="text-[11px] text-slate-500">
                    {totalCount} {isSingle ? "order" : "patient"}{totalCount === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <Table data-testid={`dose-breakdown-table-${cohort.key}`}>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Drug</TableHead>
                        <TableHead>{isSingle ? "Dose / injection" : "Bundle (dose combo)"}</TableHead>
                        <TableHead className="text-right">{isSingle ? "Orders" : "Patients"}</TableHead>
                        <TableHead className="text-right w-32">{isSingle ? "Price / vial" : "Price / bundle"}</TableHead>
                        <TableHead className="text-right w-32">Total price</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const famTotals = {};
                        const famUnitCount = {};
                        let grandTotal = 0;
                        let grandCount = 0;
                        const cells = [];
                        rows.forEach((b, i) => {
                          const prev = rows[i - 1];
                          const next = rows[i + 1];
                          const isNewFamily = !prev || prev.family !== b.family;
                          const isLastInFamily = !next || next.family !== b.family;
                          const count = isSingle ? b.orders : b.patients;
                          let unit = 0;
                          let doseCell = null;
                          let custom = false;
                          if (isSingle) {
                            unit = priceFor(b.family, b.dose_mg);
                            doseCell = <span className="font-mono font-semibold text-slate-800">{b.dose_mg ? `${b.dose_mg} mg` : "—"}</span>;
                          } else {
                            const bundle = bundleFor(b.family, cohort.months, b.doses);
                            unit = Number(bundle?.unit_price || 0);
                            custom = !bundle;
                            const doseLabel = b.doses.map((d) => `${d}mg`).join(" / ");
                            doseCell = (
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono text-xs text-slate-800">{doseLabel}</span>
                                {custom && (
                                  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300">
                                    CUSTOM
                                  </span>
                                )}
                              </div>
                            );
                          }
                          const total = unit * count;
                          famTotals[b.family] = (famTotals[b.family] || 0) + total;
                          famUnitCount[b.family] = (famUnitCount[b.family] || 0) + count;
                          grandTotal += total;
                          grandCount += count;
                          const rowKey = isSingle
                            ? `${cohort.key}-${b.family}-${b.dose_mg}`
                            : `${cohort.key}-${b.family}-${b.doses.join(",")}`;
                          cells.push(
                            <TableRow key={rowKey}
                              data-testid={`dose-breakdown-row-${cohort.key}-${i}`}
                              className={isNewFamily ? `border-t-2 ${cohort.rowAccent}` : ""}>
                              <TableCell>
                                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${FAMILY_ACCENT[b.family] || FAMILY_ACCENT.Other}`}>
                                  {b.family}
                                </span>
                              </TableCell>
                              <TableCell className="text-right">
                                {doseCell}
                              </TableCell>
                              <TableCell className="font-mono text-right font-semibold text-pink-800">
                                {count}
                              </TableCell>
                              <TableCell className="text-right">
                                <span data-testid={`dose-breakdown-price-${rowKey}`}
                                  className="font-mono text-sm text-slate-700">
                                  {unit ? `$${unit.toFixed(2)}` :
                                    (custom ? <span className="text-amber-700 text-xs">manual</span> : <span className="text-slate-300">—</span>)}
                                </span>
                              </TableCell>
                              <TableCell className="font-mono text-right font-semibold text-emerald-800">
                                {unit
                                  ? `$${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                  : (custom ? <span className="text-amber-700 text-xs">—</span> : <span className="text-slate-300">—</span>)}
                              </TableCell>
                            </TableRow>
                          );
                          if (isLastInFamily) {
                            const famTotal = famTotals[b.family];
                            cells.push(
                              <TableRow key={`${cohort.key}-${b.family}-subtotal`}
                                data-testid={`dose-breakdown-subtotal-${cohort.key}-${b.family}`}
                                className={`${cohort.subtotalBg} font-semibold`}>
                                <TableCell colSpan={2} className="text-right text-slate-700 uppercase tracking-wider text-xs">
                                  {b.family} subtotal
                                </TableCell>
                                <TableCell className="font-mono text-right text-pink-800">{famUnitCount[b.family]}</TableCell>
                                <TableCell></TableCell>
                                <TableCell className="font-mono text-right text-emerald-900">
                                  {famTotal > 0
                                    ? `$${famTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    : <span className="text-slate-300">—</span>}
                                </TableCell>
                              </TableRow>
                            );
                          }
                        });
                        cells.push(
                          <TableRow key={`${cohort.key}-grand-total`}
                            data-testid={`dose-breakdown-grand-total-${cohort.key}`}
                            className="bg-emerald-50 border-t-2 border-t-emerald-600 font-bold">
                            <TableCell colSpan={2} className="text-right text-emerald-900 uppercase tracking-wider text-xs">
                              {cohort.label} total (Semaglutide + Tirzepatide)
                            </TableCell>
                            <TableCell className="font-mono text-right text-emerald-900">{grandCount}</TableCell>
                            <TableCell></TableCell>
                            <TableCell className="font-mono text-right text-emerald-900">
                              {grandTotal > 0
                                ? `$${grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : <span className="text-slate-300">—</span>}
                            </TableCell>
                          </TableRow>
                        );
                        return cells;
                      })()}
                    </TableBody>
                  </Table>
                </div>
              </div>
            );
          })}
        </Card>
      )}

      {report && supplyBreakdown.length > 0 && (
        <Card data-testid="supply-breakdown-card" className="p-5 mb-4 border-slate-200 bg-gradient-to-br from-amber-50/40 to-white">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[11px] font-semibold text-rose-800 bg-rose-100 border border-rose-300 rounded-full px-2 py-0.5">
              MULTI-MONTH
            </span>
            <h3 className="text-sm font-semibold text-slate-800">Supply length by family</h3>
            <span className="text-[11px] text-slate-500">
              Click a chip to focus. 1-vial = 1 month · 2-vial = 2 months · 3-vial = 3 months.
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {supplyBreakdown.map((f) => (
              <div key={f.family} data-testid={`supply-summary-${f.family}`}
                className="p-3 rounded-lg border border-slate-200 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${FAMILY_ACCENT[f.family] || FAMILY_ACCENT.Other}`}>
                    {f.family}
                  </span>
                  <span className="text-[11px] text-slate-500">{f.patients} pts · {f.total} orders</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <button data-testid={`supply-chip-${f.family}-1`}
                    onClick={() => { setDrugFilter(f.family); setVialFilter("1"); }}
                    className="p-2 rounded border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-left">
                    <div className="text-[10px] uppercase font-semibold text-emerald-800 tracking-wider">1 mo</div>
                    <div className="text-lg font-mono font-bold text-emerald-900">{f.one}</div>
                  </button>
                  <button data-testid={`supply-chip-${f.family}-2`}
                    onClick={() => { setDrugFilter(f.family); setVialFilter("2"); }}
                    className="p-2 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-left">
                    <div className="text-[10px] uppercase font-semibold text-amber-800 tracking-wider">2 mo</div>
                    <div className="text-lg font-mono font-bold text-amber-900">{f.two}</div>
                  </button>
                  <button data-testid={`supply-chip-${f.family}-3`}
                    onClick={() => { setDrugFilter(f.family); setVialFilter("3"); }}
                    className="p-2 rounded border border-rose-300 bg-rose-50 hover:bg-rose-100 text-left">
                    <div className="text-[10px] uppercase font-semibold text-rose-800 tracking-wider">3 mo</div>
                    <div className="text-lg font-mono font-bold text-rose-900">{f.three}</div>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {report && report.by_family?.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {report.by_family.map((fam) => (
            <Card key={fam.family} data-testid={`family-card-${fam.family}`} className="p-5 border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Syringe className="w-4 h-4" style={{ color: FAMILY_COLOR[fam.family] || "#64748b" }} />
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${FAMILY_ACCENT[fam.family] || FAMILY_ACCENT.Other}`}>{fam.family}</span>
                  <button onClick={() => setDrugFilter(drugFilter === fam.family ? "all" : fam.family)}
                    className="text-[11px] text-pink-700 hover:underline ml-1"
                    data-testid={`family-filter-${fam.family}`}>
                    {drugFilter === fam.family ? "clear filter" : "filter"}
                  </button>
                </div>
                <div className="text-xs text-slate-500">
                  {fam.patients} pts · {num(fam.vials)} vials · {num(fam.monthly_mg)} mg/mo · {fam.multi_vial} multi-vial
                </div>
              </div>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={fam.buckets} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip cursor={{ fill: "#f1f5f9" }}
                    formatter={(v, key) => key === "patients" ? [v, "Patients"] : [`${num(v)} mg/mo`, "Total mg/mo"]} />
                  <Legend wrapperStyle={{ fontSize: "10px" }} />
                  <Bar dataKey="patients" name="Patients" fill={FAMILY_COLOR[fam.family] || "#64748b"} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <p className="text-[10px] text-slate-400 mt-1">Weekly-mg dose buckets (titration ladder)</p>
            </Card>
          ))}
        </div>
      )}

      {report && (
        <Card className="p-0 overflow-hidden border-slate-200">
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100">
            <Input data-testid="patient-orders-search" placeholder="Search patient or drug…"
              value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs h-9" />
            <div className="flex flex-wrap gap-1.5">
              {["all", ...report.by_family.map((f) => f.family)].map((fam) => (
                <button key={fam} data-testid={`drug-chip-${fam}`}
                  onClick={() => setDrugFilter(fam)}
                  className={`text-xs px-2.5 py-1 rounded-full border ${
                    drugFilter === fam
                      ? "bg-pink-700 text-white border-pink-700"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}>{fam === "all" ? "All drugs" : fam}</button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {["all", "0-2.5", "2.5-5", "5-7.5", "7.5-10", "10-12.5", "12.5-15", "15+"].map((b) => (
                <button key={b} data-testid={`bucket-chip-${b}`}
                  onClick={() => setBucketFilter(b)}
                  className={`text-[11px] px-2 py-1 rounded-full border font-mono ${
                    bucketFilter === b
                      ? "bg-slate-800 text-white border-slate-800"
                      : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                  }`}>{b === "all" ? "any dose" : `${b} mg`}</button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[
                { v: "all", label: "Any supply" },
                { v: "1", label: "1-vial · 1 mo" },
                { v: "2", label: "2-vial · 2 mo" },
                { v: "3", label: "3-vial · 3 mo" },
              ].map((o) => (
                <button key={o.v} data-testid={`vial-chip-${o.v}`}
                  onClick={() => setVialFilter(o.v)}
                  className={`text-[11px] px-2 py-1 rounded-full border font-medium ${
                    vialFilter === o.v
                      ? (o.v === "1" ? "bg-emerald-700 text-white border-emerald-700"
                        : o.v === "2" ? "bg-amber-600 text-white border-amber-600"
                        : o.v === "3" ? "bg-rose-700 text-white border-rose-700"
                        : "bg-slate-800 text-white border-slate-800")
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}>{o.label}</button>
              ))}
            </div>
            <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
              <input data-testid="multi-only-toggle" type="checkbox" checked={multiOnly}
                onChange={(e) => setMultiOnly(e.target.checked)} className="accent-pink-700" />
              Multi-vial only
            </label>
            <span className="text-xs text-slate-500 ml-2">{filtered.length} shown</span>
          </div>
          {filtered.length === 0 ? (
            <EmptyState icon={Users} title="No rows match your filters" />
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="patient-orders-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="cursor-pointer" onClick={() => clickSort("patient_last")}>Patient{sortIcon("patient_last")}</TableHead>
                    <TableHead className="cursor-pointer" onClick={() => clickSort("order_id")}>Order ID{sortIcon("order_id")}</TableHead>
                    <TableHead className="cursor-pointer" onClick={() => clickSort("order_date")}>Order Date{sortIcon("order_date")}</TableHead>
                    <TableHead className="cursor-pointer" onClick={() => clickSort("drug_family")}>Drug{sortIcon("drug_family")}</TableHead>
                    <TableHead className="text-right cursor-pointer" onClick={() => clickSort("vials")}>mL{sortIcon("vials")}</TableHead>
                    <TableHead className="text-right cursor-pointer" onClick={() => clickSort("dose_per_injection_mg")}>Dose / injection{sortIcon("dose_per_injection_mg")}</TableHead>
                    <TableHead className="text-right">Sig</TableHead>
                    <TableHead className="text-right cursor-pointer" onClick={() => clickSort("weekly_mg")}>mg / wk{sortIcon("weekly_mg")}</TableHead>
                    <TableHead className="text-right cursor-pointer" onClick={() => clickSort("monthly_mg")}>mg / mo{sortIcon("monthly_mg")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r, i) => {
                    const prev = filtered[i - 1];
                    const samePatientAsPrev = prev
                      && prev.patient_last === r.patient_last
                      && prev.patient_first === r.patient_first;
                    const isGrouped = r.patient_row_count > 1;
                    const sameDrugAsPrev = prev && samePatientAsPrev && prev.drug === r.drug;
                    const isMultiVialGroup = r.drug_line_count > 1;
                    return (
                    <TableRow key={`${r.patient_last}-${r.patient_first}-${r.drug}-${i}`}
                      data-testid={`patient-orders-row-${i}`}
                      className="hover:bg-slate-50/60">
                      <TableCell className="font-medium text-slate-800 whitespace-nowrap">
                        <button type="button" data-testid={`row-patient-filter-${i}`}
                          onClick={() => setQ(r.patient)}
                          title="Click to filter to this patient"
                          className="text-slate-800 hover:text-pink-700 hover:underline focus:outline-none">
                          {r.patient}
                        </button>
                        <button type="button" data-testid={`row-family-filter-badge-${i}`}
                          onClick={() => setDrugFilter(r.drug_family)}
                          title={`Filter to ${r.drug_family} only`}
                          className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full border font-medium hover:opacity-80 ${FAMILY_ACCENT[r.drug_family] || FAMILY_ACCENT.Other}`}>
                          {r.drug_family}
                        </button>
                        {(r.drug_line_count || 0) > 1 && (
                          <span className="ml-2 text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-800 border border-rose-300"
                            title={`${r.drug_line_count} orders of this drug for this patient — multi-vial`}>
                            {r.drug_line_count}× MULTI-VIAL
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-600 whitespace-nowrap" data-testid={`row-order-id-${i}`}>
                        {r.order_id || <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-600 whitespace-nowrap" data-testid={`row-order-date-${i}`}>
                        {r.order_date || <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600" title={r.drug}>
                        <button type="button" data-testid={`row-family-filter-${i}`}
                          onClick={() => setDrugFilter(r.drug_family)}
                          title={`Filter to ${r.drug_family} only`}
                          className="font-medium text-slate-800 hover:text-pink-700 hover:underline focus:outline-none">
                          {r.drug_family}
                        </button>
                      </TableCell>
                      <TableCell className="font-mono text-right">{num(r.vials)}</TableCell>
                      <TableCell className="font-mono text-right text-xs">
                        {r.dose_per_injection_mg
                          ? <span className="font-semibold text-slate-800">{r.dose_per_injection_mg} mg</span>
                          : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="font-mono text-right text-xs text-slate-700" title={r.sig || ""}>
                        {(() => {
                          const u = r.dose_units ?? r.weekly_units;
                          return (u != null && u !== "" && Number(u) > 0)
                            ? <><span className="font-semibold">{u}</span> <span className="text-slate-400">units</span></>
                            : <span className="text-slate-300">—</span>;
                        })()}
                      </TableCell>
                      <TableCell className="font-mono text-right text-pink-800">{num(r.weekly_mg)}</TableCell>
                      <TableCell className="font-mono text-right text-pink-800 font-semibold">{num(r.monthly_mg)}</TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      )}

      <Dialog open={showPricing} onOpenChange={(o) => !o && setShowPricing(false)}>
        <DialogContent data-testid="set-pricing-dialog" className="sm:max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-amber-700" /> Set Dose Pricing
          </DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 mb-3">
            Single-month prices are per vial. 2- and 3-month bundles are priced by <b>dose combination</b>
            &nbsp;(titration bundles), matching Amended Exhibit A. Vera looks up the exact combo when it
            builds an invoice; patients whose doses don't match a listed combo show as
            <span className="mx-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300">CUSTOM</span>
            so you can set a manual price. Need a combo the pharmacy prices but Exhibit A doesn't list?
            Use <b>+ Add combo</b> below any bundle table to save your own — it'll be picked up on future invoices.
          </p>

          {/* ─── Single-Month table ─── */}
          <div className="mb-6">
            <div className="text-sm font-heading font-semibold text-slate-800 mb-2 flex items-center gap-2">
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-900 border-emerald-300">1-vial · Single-month</span>
              <span className="text-[11px] text-slate-500">Per-vial price for each dose</span>
            </div>
            {["Semaglutide", "Tirzepatide"].map((family) => {
              const doses = [...new Set(Object.keys(pricingDraft)
                .filter((k) => k.startsWith(`${family}|`))
                .map((k) => Number(k.split("|")[1])))]
                .sort((a, b) => a - b);
              return (
                <div key={family} className="mb-3">
                  <div className="text-xs font-heading font-semibold text-slate-700 mb-1.5 flex items-center gap-2">
                    <Syringe className="w-3.5 h-3.5 text-pink-700" /> {family}
                  </div>
                  <Table data-testid={`pricing-table-single-${family}`}>
                    <TableHeader><TableRow className="bg-slate-50">
                      <TableHead>Dose / injection</TableHead>
                      <TableHead className="text-right">Price / vial</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {doses.map((dose) => (
                        <TableRow key={`${family}-${dose}`} data-testid={`pricing-row-single-${family}-${dose}`}>
                          <TableCell className="font-mono font-semibold text-slate-800">{dose} mg</TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex items-center gap-0.5">
                              <span className="text-slate-400 text-xs">$</span>
                              <input
                                data-testid={`pricing-input-single-${family}-${dose}`}
                                type="number" step="0.01" min="0"
                                value={pricingDraft[`${family}|${dose}`] ?? ""}
                                onChange={(e) => setPricingDraft((prev) => ({
                                  ...prev,
                                  [`${family}|${dose}`]: Number(e.target.value || 0),
                                }))}
                                placeholder="0.00"
                                className="font-mono text-right text-sm w-24 px-2 py-1 rounded border border-slate-200 focus:border-amber-700 focus:outline-none focus:ring-1 focus:ring-amber-700"
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              );
            })}
          </div>

          {/* ─── 2-Month & 3-Month Bundle tables ─── */}
          {[2, 3].map((months) => {
            const chip = months === 2
              ? "bg-amber-100 text-amber-900 border-amber-300"
              : "bg-rose-100 text-rose-900 border-rose-300";
            return (
              <div key={`bundles-${months}`} className="mb-6">
                <div className="text-sm font-heading font-semibold text-slate-800 mb-2 flex items-center gap-2">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${chip}`}>
                    {months}-vial · {months}-month bundle
                  </span>
                  <span className="text-[11px] text-slate-500">Price is per bundle (all vials in the combo)</span>
                </div>
                {["Semaglutide", "Tirzepatide"].map((family) => {
                  const rows = (bundlesDraft || [])
                    .map((b, idx) => ({ b, idx }))
                    .filter(({ b }) => b.family === family && Number(b.months) === months)
                    .sort((a, c) => {
                      const ad = a.b.doses || [];
                      const cd = c.b.doses || [];
                      for (let i = 0; i < Math.max(ad.length, cd.length); i++) {
                        const d = (ad[i] ?? 0) - (cd[i] ?? 0);
                        if (d) return d;
                      }
                      return 0;
                    });
                  const doseOptions = familyDoses(family);
                  const isAddingHere = addingBundle && addingBundle.family === family && Number(addingBundle.months) === months;
                  return (
                    <div key={`${family}-${months}`} className="mb-4">
                      <div className="text-xs font-heading font-semibold text-slate-700 mb-1.5 flex items-center gap-2">
                        <Syringe className="w-3.5 h-3.5 text-pink-700" /> {family}
                      </div>
                      <Table data-testid={`pricing-table-bundle-${months}-${family}`}>
                        <TableHeader><TableRow className="bg-slate-50">
                          <TableHead>Bundle (dose combo)</TableHead>
                          <TableHead>Label</TableHead>
                          <TableHead className="text-right w-32">Price / bundle</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {rows.length === 0 && !isAddingHere && (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center text-slate-400 text-sm py-4">
                                No {months}-month bundles for {family} yet — click <b>Add combo</b> below to create one.
                              </TableCell>
                            </TableRow>
                          )}
                          {rows.map(({ b, idx }) => {
                            const def = isDefaultBundle(b);
                            return (
                            <TableRow key={`bundle-${idx}`}
                              data-testid={`pricing-row-bundle-${months}-${family}-${(b.doses || []).join("-")}`}>
                              <TableCell className="font-mono font-semibold text-slate-800 whitespace-nowrap">
                                {(b.doses || []).map((d) => `${d}mg`).join(" / ")}
                                {!def && (
                                  <span className="ml-2 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-900 border border-sky-300">
                                    CUSTOM
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-xs">
                                <input
                                  data-testid={`pricing-input-bundle-label-${months}-${family}-${(b.doses || []).join("-")}`}
                                  type="text"
                                  value={b.label || ""}
                                  onChange={(e) => setBundlesDraft((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], label: e.target.value };
                                    return next;
                                  })}
                                  placeholder="Label (optional)"
                                  className="text-xs w-full max-w-[260px] px-2 py-1 rounded border border-slate-200 focus:border-amber-700 focus:outline-none focus:ring-1 focus:ring-amber-700"
                                />
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="inline-flex items-center gap-0.5">
                                  <span className="text-slate-400 text-xs">$</span>
                                  <input
                                    data-testid={`pricing-input-bundle-${months}-${family}-${(b.doses || []).join("-")}`}
                                    type="number" step="0.01" min="0"
                                    value={b.unit_price ?? ""}
                                    onChange={(e) => setBundlesDraft((prev) => {
                                      const next = [...prev];
                                      next[idx] = { ...next[idx], unit_price: Number(e.target.value || 0) };
                                      return next;
                                    })}
                                    placeholder="0.00"
                                    className="font-mono text-right text-sm w-24 px-2 py-1 rounded border border-slate-200 focus:border-amber-700 focus:outline-none focus:ring-1 focus:ring-amber-700"
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="text-right">
                                {def ? (
                                  <span title="Default combos are protected — edit price only" className="text-slate-300 text-xs">—</span>
                                ) : (
                                  <button
                                    type="button"
                                    data-testid={`pricing-remove-bundle-${months}-${family}-${(b.doses || []).join("-")}`}
                                    onClick={() => removeBundle(b)}
                                    className="text-slate-400 hover:text-rose-600"
                                    title="Remove this custom combo"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </TableCell>
                            </TableRow>
                            );
                          })}
                          {isAddingHere && (
                            <TableRow className="bg-amber-50/60"
                              data-testid={`pricing-add-bundle-row-${months}-${family}`}>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  {Array.from({ length: months }).map((_, slot) => (
                                    <select
                                      key={`add-dose-${slot}`}
                                      data-testid={`pricing-add-bundle-dose-${months}-${family}-${slot}`}
                                      value={addingBundle.doses[slot] || ""}
                                      onChange={(e) => setAddDose(slot, e.target.value)}
                                      className="text-xs px-1.5 py-1 rounded border border-slate-200 bg-white focus:border-amber-700 focus:outline-none focus:ring-1 focus:ring-amber-700"
                                    >
                                      <option value="">dose {slot + 1}</option>
                                      {doseOptions.map((d) => (
                                        <option key={d} value={d}>{d} mg</option>
                                      ))}
                                    </select>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell className="text-xs">
                                <input
                                  data-testid={`pricing-add-bundle-label-${months}-${family}`}
                                  type="text"
                                  value={addingBundle.label}
                                  onChange={(e) => setAddingBundle((prev) => ({ ...prev, label: e.target.value }))}
                                  placeholder="Label (optional, e.g. Sema 60-Day .5/1mg)"
                                  className="text-xs w-full max-w-[260px] px-2 py-1 rounded border border-slate-200 focus:border-amber-700 focus:outline-none focus:ring-1 focus:ring-amber-700"
                                />
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="inline-flex items-center gap-0.5">
                                  <span className="text-slate-400 text-xs">$</span>
                                  <input
                                    data-testid={`pricing-add-bundle-price-${months}-${family}`}
                                    type="number" step="0.01" min="0"
                                    value={addingBundle.unit_price}
                                    onChange={(e) => setAddingBundle((prev) => ({ ...prev, unit_price: e.target.value }))}
                                    placeholder="0.00"
                                    className="font-mono text-right text-sm w-24 px-2 py-1 rounded border border-slate-200 focus:border-amber-700 focus:outline-none focus:ring-1 focus:ring-amber-700"
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="text-right whitespace-nowrap">
                                <div className="inline-flex items-center gap-1">
                                  <button
                                    type="button"
                                    data-testid={`pricing-add-bundle-confirm-${months}-${family}`}
                                    onClick={confirmAddBundle}
                                    className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold px-2 py-1 rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100"
                                    title="Add this combo to the table"
                                  >
                                    Add
                                  </button>
                                  <button
                                    type="button"
                                    data-testid={`pricing-add-bundle-cancel-${months}-${family}`}
                                    onClick={() => setAddingBundle(null)}
                                    className="text-slate-400 hover:text-slate-700 text-xs px-1.5 py-1"
                                    title="Cancel"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                      {!isAddingHere && (
                        <button
                          type="button"
                          data-testid={`pricing-add-bundle-${months}-${family}`}
                          onClick={() => openAddBundle(family, months)}
                          className="mt-1.5 text-[11px] text-amber-700 hover:text-amber-900 font-semibold inline-flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" /> Add {family} {months}-month combo
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

          <DialogFooter>
            <button data-testid="pricing-reset-in-dialog" onClick={resetPricingToDefaults}
              className="text-[11px] text-slate-500 hover:text-amber-700 underline mr-auto">
              Reset to pharmacy defaults
            </button>
            <Button variant="outline" onClick={() => setShowPricing(false)}>Cancel</Button>
            <Button data-testid="pricing-save-btn" onClick={savePricingDraft}
              className="bg-amber-700 hover:bg-amber-800 text-white">Save Pricing</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
